import { ChangeEvent, SyntheticEvent, useRef, useState } from 'react';
import {
  useRecoilCallback, useRecoilState, useRecoilValue,
} from 'recoil';
import { useConditionalEffect, usePrevious } from 'react-delta';
import { useMutation } from '@apollo/client';
import isEqual from 'lodash/isEqual';
import difference from 'lodash/difference';
import cloneDeep from 'lodash/cloneDeep';
import some from 'lodash/some';
import BootstrapForm from 'react-bootstrap/Form';

import * as K from 'common/constants';
import ELEMENT, { createTestId } from 'common/element';
import { SET_DISPLAY_SETTINGS } from 'common/queries/apolloQueries';
import { Mutation, Scope, SortOrder, SystemField } from 'common/graphqlTypes';
import log from 'common/ScalyrLogger';
import { processServerSettings, ServerSettings } from 'common/util/settings';
import { savedSettingsState, settingsState } from 'components/common/Managers/SettingsManager.state';
import { useResetScrollState } from 'hooks/useResetScrollState';

import Button, { ButtonRef, ButtonVariant } from 'components/common/Button/Button';
import CheckboxRadio from 'components/common/CheckboxRadio/CheckboxRadio';
import Chooser from 'components/common/Chooser/Chooser';
import Dialog, { Callback, CustomDialogHeaderOrFooter } from 'components/common/Dialog/Dialog';
import Select, { OptionType } from 'components/common/ReactSelect/Select';
import { accountDataState, hasTeamsState } from 'components/account/account.state';
import Badge from 'components/common/Badge/Badge';
import { facetListState } from 'components/search/facets.state';

import './LogViewSettingsDialog.scss';

const GLOBAL_SAVE_OPTIONS = [
  { label: 'This account', value: 'one', id: 'hello' },
  { label: 'All accounts', value: 'all' },
];

type LogViewSettingsDialogProps = {
  show: boolean;
  close: () => void;
};

/**
 * This component allows the user to change and optionally save settings that affect how log lines are displayed.
 *
 * @prop show     if true, the dialog is displayed
 * @prop close    function to call to close the dialog
 */

function LogViewSettingsDialog({
  show,
  close,
}: LogViewSettingsDialogProps): JSX.Element {

  const [ settings, setSettings ] = useRecoilState(settingsState);
  const hasTeams = useRecoilValue(hasTeamsState);
  const account = useRecoilValue(accountDataState);
  const facetList = useRecoilValue(facetListState);

  const displayMode = settings[K.SETTING.DISPLAY_MODE];
  const sortOrder = settings[K.SETTING.SORT_ORDER];
  const systemFields = [ ...settings[K.SETTING.SYSTEM_FIELDS] ];
  const additionalFields = [ ...settings[K.SETTING.ADDITIONAL_FIELDS] ];
  const fontFamily = settings[K.SETTING.FONT_FAMILY];

  const [ displayModeDraft, setDisplayModeDraft ] = useState(displayMode);
  const [ systemFieldsDraft, setSystemFieldsDraft ] = useState(systemFields);
  const [ additionalFieldsDraft, setAdditionalFieldsDraft ] = useState(additionalFields);
  const [ availableFieldsDraft, setAvailableFieldsDraft ] = useState<string[]>([]);
  const [ sortOrderDraft, setSortOrderDraft ] = useState(sortOrder);
  const [ fontFamilyDraft, setFontFamilyDraft ] = useState(fontFamily);
  const [ saveToAccount, setSaveToAccount ] = useState(false);
  const [ saveToAllAccounts, setSaveToAllAccounts ] = useState(false);
  const [ savedSettings, setSavedSettings ] = useRecoilState(savedSettingsState);
  const resetScrollState = useResetScrollState();

  const okButtonRef = useRef<ButtonRef>(null) as ButtonRef;

  log.debug(`LogViewSettingsDialog: ${facetList.length} current fields`);

  const settingsChanged = some(K.DISPLAY_SETTINGS, (displaySetting) => !isEqual(settings[displaySetting], savedSettings[displaySetting]));

  // reset chooser lists to match current data
  const resetChooserFields = (): void => {
    setAvailableFieldsDraft(difference(facetList, additionalFields));
    setAdditionalFieldsDraft(additionalFields);
  };

  // Reset our draft values when opening the dialog (when show is changing from false to true)
  const prevShow = usePrevious(show);
  useConditionalEffect(() => {
    setDisplayModeDraft(displayMode);
    setFontFamilyDraft(fontFamily);
    setSortOrderDraft(sortOrder);
    setSystemFieldsDraft(systemFields);
    resetChooserFields();
  }, !prevShow && show);

  // update chooser if data changes
  const prevFacetList = usePrevious(facetList);
  const prevAdditionalFields = usePrevious(additionalFields);
  useConditionalEffect(() => {
    resetChooserFields();
  }, !isEqual(prevFacetList, facetList) || !isEqual(prevAdditionalFields, additionalFields));

  // Once the save settings server mutation is complete, we update the settings in global state.
  const onCompleted = useRecoilCallback(({ set, snapshot }) => (newServerSettings: Mutation): void => {
    const currentSortOrder = snapshot.getLoadable(settingsState).getValue()[K.SETTING.SORT_ORDER];
    const newSortOrder = newServerSettings.setDisplaySettings[K.SETTING.SORT_ORDER];
    const isSortOrderChanging = currentSortOrder !== newSortOrder;
    set(settingsState, (currentSettings) => processServerSettings(currentSettings, newServerSettings.setDisplaySettings as ServerSettings));
    if (saveToAccount) {
      setSavedSettings(processServerSettings(K.DEFAULT_SETTINGS, newServerSettings.setDisplaySettings as ServerSettings));
    }
    resetScrollState(isSortOrderChanging);
    setSaveToAccount(false); // checkbox is unchecked by default
  }, []);

  // save settings to server
  const [ setDisplaySettings ] = useMutation(SET_DISPLAY_SETTINGS, {
    onCompleted,
  });

  // save settings to memory, account, or account across teams
  const saveSettings = (): void => {

    const newDisplaySettings = {
      showLeftNavExpanded: settings[K.SETTING.SHOW_SIDEBAR],
      displayMode:         displayModeDraft,
      sortOrder:           sortOrderDraft,
      fontFamily:          fontFamilyDraft,
      additionalFields:    additionalFieldsDraft,
      systemFields:        systemFieldsDraft,
    };

    const newSettings = cloneDeep(settings);
    newSettings[K.SETTING.DISPLAY_MODE] = displayModeDraft;
    newSettings[K.SETTING.SORT_ORDER] = sortOrderDraft;
    newSettings[K.SETTING.FONT_FAMILY] = fontFamilyDraft;
    newSettings[K.SETTING.SYSTEM_FIELDS] = [ ...systemFieldsDraft ];
    newSettings[K.SETTING.ADDITIONAL_FIELDS] = [ ...additionalFieldsDraft ];

    if (saveToAccount) {
      setDisplaySettings({ variables: { scope: Scope.account, settings: newDisplaySettings } });
      if (saveToAllAccounts) {
        setDisplaySettings({ variables: { scope: Scope.global, settings: newDisplaySettings } });
      }
    } else {
      // instead of saving to the login session on the server, we save to memory
      setSettings(newSettings);
      const isSortOrderChanging = settings[K.SETTING.SORT_ORDER] !== sortOrderDraft;
      resetScrollState(isSortOrderChanging);
    }
  };

  if (!show) {
    return <></>;
  }

  const restoreSettings = (ev: SyntheticEvent): void => {
    setDisplayModeDraft(savedSettings[K.SETTING.DISPLAY_MODE]);
    setSortOrderDraft(savedSettings[K.SETTING.SORT_ORDER]);
    setFontFamilyDraft(savedSettings[K.SETTING.FONT_FAMILY]);
    setSystemFieldsDraft([ ...savedSettings[K.SETTING.SYSTEM_FIELDS] ]);
    setAdditionalFieldsDraft([ ...savedSettings[K.SETTING.ADDITIONAL_FIELDS] ]);
    ev.preventDefault();
  };

  // no need to do anything on cancel
  const cancelHandler = (): void => {};

  // returns a function that closes the dialog before calling the given function
  const closeBefore = (callback?: Callback): (() => void) => (): void => {
    close();
    if (callback) {
      callback();
    }
  };

  const handleCheckboxChange = (setter: (value: boolean) => void) => (ev: SyntheticEvent): void => {
    const inputElement = ev.target as HTMLInputElement;
    setter(inputElement.checked);
  };

  // if user has teams, offer option to save to current or to all teams
  const checkboxLabel = hasTeams ? 'Use as default for' : 'Use as default';
  const onDefaultChange = (value: string): void => setSaveToAllAccounts(value === GLOBAL_SAVE_OPTIONS[1].value);

  // Note: react-select does not accept extra props, so we have no easy way to give it or its options test IDs
  const saveToAccountSection = !account.isSingularityXDRUser && (
    <div className="LogViewSettingsDialog__SaveOptions">
      <CheckboxRadio
        type="checkbox"
        label={checkboxLabel}
        onChange={handleCheckboxChange(setSaveToAccount)}
        checked={saveToAccount}
        id="LogViewSettingsDialog-account-checkbox"
        testId={ELEMENT.settingsDialogUseAsDefault}
      />
      {hasTeams && (
        <Select
          options={GLOBAL_SAVE_OPTIONS}
          onChange={(selectedOption) => {
            const { value } = selectedOption as OptionType;
            onDefaultChange(value as string);
          }}
          placement="top"
        />
      )}
    </div>
  );

  // custom footer with three buttons
  const footer: CustomDialogHeaderOrFooter = {
    leftItems: [
      saveToAccountSection,
    ],
    rightItems: [
      <Button
        variant={ButtonVariant.PRIMARY}
        onClick={closeBefore(saveSettings)}
        testId={ELEMENT.settingsDialogOk}
        buttonRef={okButtonRef}
      >
        OK
      </Button>,
      <Button onClick={closeBefore(cancelHandler)} testId={ELEMENT.settingsDialogCancel}>Cancel</Button>,
    ],
    className: 'LogViewSettingsDialog__Footer',
  };

  // radio buttons for choosing display mode

  const onDisplayModeChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setDisplayModeDraft(event.target.value as K.DISPLAY_MODE);
  };

  const displayModeText = {
    [K.DISPLAY_MODE.LINES]:   'Single-line rows',
    [K.DISPLAY_MODE.WRAPPED]: 'Wrapping rows',
    [K.DISPLAY_MODE.TABLE]:   'Table (select columns below)',
  };

  const displayModesList = [ K.DISPLAY_MODE.LINES, K.DISPLAY_MODE.WRAPPED, K.DISPLAY_MODE.TABLE ];
  const displayModeSection = displayModesList.map((mode) => {
    const id = 'LogViewSettingsDialog__Mode--' + mode;
    return (
      <CheckboxRadio
        type="radio"
        value={mode}
        id={id}
        key={id}
        label={displayModeText[mode]}
        checked={displayModeDraft === mode}
        onChange={onDisplayModeChange}
        testId={createTestId(ELEMENT.settingsDialogMode, mode)}
      />
    );
  });

  // radio buttons for choosing font

  const onFontChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setFontFamilyDraft(event.target.value as K.FONT_FAMILY);
  };
  const fontText = {
    [K.FONT_FAMILY.REGULAR]: 'Regular',
    [K.FONT_FAMILY.MONOSPACE]: 'Monospace',
  };

  const fontList = [ K.FONT_FAMILY.REGULAR, K.FONT_FAMILY.MONOSPACE ];
  const fontSection = fontList.map((font) => {
    const id = 'LogViewSettingsDialog__Font--' + font;
    return (
      <CheckboxRadio
        type="radio"
        value={font}
        id={id}
        key={id}
        label={fontText[font]}
        checked={fontFamilyDraft === font}
        onChange={onFontChange}
        testId={createTestId(ELEMENT.settingsDialogMode, font)}
      />
    );
  });
  // radio buttons for choosing sort order

  const onSortOrderChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSortOrderDraft(event.target.value as SortOrder);
  };

  const sortOrderText = {
    [SortOrder.MOST_RECENT_AT_TOP]:     'Most recent at top',
    [SortOrder.MOST_RECENT_AT_BOTTOM]:  'Most recent at bottom',
  };

  const sortOrderList = [ SortOrder.MOST_RECENT_AT_TOP, SortOrder.MOST_RECENT_AT_BOTTOM ];
  const sortOrderSection = sortOrderList.map((order) => {
    const id = 'LogViewSettingsDialog__SortOrder--' + order;
    return (
      <CheckboxRadio
        type="radio"
        value={order}
        id={id}
        key={id}
        label={sortOrderText[order]}
        checked={sortOrderDraft === order}
        onChange={onSortOrderChange}
        testId={createTestId(ELEMENT.settingsDialogMode, order)}
      />
    );
  });

  // checkboxes for choosing which system fields to display

  const onSystemFieldChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const target = event.target;
    setSystemFieldsDraft(target.checked ? systemFieldsDraft.concat(target.value) : systemFieldsDraft.filter((field: string) => field !== target.value));
  };

  const systemFieldsList = [ SystemField.date, SystemField.timestamp, SystemField.host, SystemField.logfile, SystemField.rawLogLine ];
  const systemFieldsSection = systemFieldsList.map((field) => {
    const id = 'LogViewSettingsDialog__System--' + field;
    return (
      <CheckboxRadio
        type="checkbox"
        id={id}
        key={id}
        label={K.SystemFieldText[field]}
        value={field}
        checked={systemFieldsDraft.includes(field)}
        onChange={onSystemFieldChange}
        testId={createTestId(ELEMENT.settingsDialogSystemField, field)}
      />
    );
  });

  let restoreBadge = null;
  if (settingsChanged) {
    const restoreLink = (
      <a
        className="LogViewSettingsDialog__RestoreLink"
        onClick={(ev: SyntheticEvent): void => restoreSettings(ev)}
        href="#"
        data-test-id={ELEMENT.settingsDialogRestore}
        role="button"
        tabIndex={0}
      >
        Restore default settings
      </a>
    );
    restoreBadge = (
      <Badge variant="info" className="LogViewSettingsDialog__Restore">
        Current settings are not your defaults.
        {restoreLink}
      </Badge>
    );
  }

  return (
    <Dialog
      title="Display Settings"
      show={show}
      className="LogViewSettingsDialog"
      close={close}
      cancelCallback={cancelHandler}
      footer={footer}
      focusButtonRef={okButtonRef}
      onEnterCallback={saveSettings}
      testId={ELEMENT.settingsDialog}
    >
      {restoreBadge}
      <div className="LogViewSettingsDialog__Body">
        <BootstrapForm>
          <div>
            <div className="LogViewSettingsDialog__Header">Show Results As</div>
            <BootstrapForm.Group>
              {displayModeSection}
            </BootstrapForm.Group>
          </div>
          <div>
            <div className="LogViewSettingsDialog__Header">System Fields</div>
            <BootstrapForm.Group>
              {systemFieldsSection}
            </BootstrapForm.Group>
          </div>
          <Chooser
            sourceItems={availableFieldsDraft}
            targetItems={additionalFieldsDraft}
            sourceTitle="Available Fields:"
            targetTitle="Fields to Show:"
            onSourceChange={(fields: string[]): void => setAvailableFieldsDraft(fields)}
            onTargetChange={(fields: string[]): void => setAdditionalFieldsDraft(fields)}
            sortTarget={false}
            maxItemLength={30}
            testId={ELEMENT.settingsDialogChooser}
          />
          <div className="LogViewSettingsDialog__FormSection">
            <div className="LogViewSettingsDialog__Header">Order of Results</div>
            <BootstrapForm.Group>
              {sortOrderSection}
            </BootstrapForm.Group>
          </div>
          <div className="LogViewSettingsDialog__FormSection">
            <div className="LogViewSettingsDialog__Header">Font</div>
            <BootstrapForm.Group>
              {fontSection}
            </BootstrapForm.Group>
          </div>
        </BootstrapForm>
      </div>
    </Dialog>
  );
}

export default LogViewSettingsDialog;
